import React, { useContext, useCallback, useMemo, useState, Fragment, ReactText } from 'react'
import { NoteComment, NoteTypeWithStatus } from 'shared/components/Notes/models/note'
import { ParticipantTypeWithStatus } from 'pages/Participants/model/participant'
import FilterDialog from './components/Dialogs/FilterDialog'
import SearchField from 'shared/components/SearchField'
import { lighten } from 'polished'
import { useDispatch, useSelector } from 'react-redux'
import { roleSelector, authSelector, organizationSettingSelector } from 'shared/hocs/withAuth'
import useRouter from 'shared/hooks/useRouter'
import usePageTable, { UsePageTableHandlers } from 'shared/hooks/usePageTable'
import useQuery from 'shared/hooks/useQuery'
import dayjs from 'dayjs'
import cn from 'classnames'
import upperFirst from 'lodash/upperFirst'
import { Tooltipped } from 'react-md'
import styled, { ThemeContext } from 'styled-components'
import Button from 'shared/components/Button'
import Box from 'shared/components/Box'
import MoreOptions from 'shared/components/DataTable/MoreOptions'
import Pagination from 'shared/components/DataTable/Pagination'
import Avatar from 'pages/Parent/components/Avatar'
import SvgLocationIcon from 'assets/icons/Location'
import SvgCalendarIcon from 'assets/icons/Calendar'
import SvgCoachIcon from 'assets/icons/Coaches'
import SvgTimeIcon from 'assets/icons/Time'
import SvgPencilIcon from 'assets/icons/EditInformation'
import SvgArchiveIcon from 'assets/icons/Archive'
import SvgVisibilityIcon from 'assets/icons/Visibility'
import SvgNoteIcon from 'assets/icons/Note'
import SvgFlagIcon from 'assets/icons/Flag'
import useNoteActions, { Preview } from 'shared/components/Notes/useNoteActions'
import DefaultImage from 'assets/images/logo-small.png'
import FilterIcon from 'assets/icons/Filter'
import { showDialog, hideAllDialogs } from 'shared/redux/app/reducer'
import {
  FLAG_WARNING_COLOR,
  FLAG_DANGER_COLOR,
  FLAG_RESOLVED_WARNING_COLOR,
  FLAG_RESOLVED_DANGER_COLOR,
} from 'shared/constants/participant'

type NoteProps = {
  participant?: ParticipantTypeWithStatus
  refetchParticipant?: () => void
}

function Notes(props: NoteProps) {
  const theme = useContext(ThemeContext)
  const dispatch = useDispatch()
  const router = useRouter<{ id: string }>()
  const { id: param_user_id } = router.params
  const [filterSort, setFilterSort] = useState<'asc' | 'desc'>('desc')
  const [filterId, setFilterId] = useState('All Notes')
  const [filterNoteCategory, setFilterNoteCategory] = useState<string>('all')
  const { auth: user } = useSelector(authSelector)
  const { isAdmin, isOrganization } = useSelector(roleSelector)

  const { participant, refetchParticipant } = props
  const user_id = useMemo(() => {
    return param_user_id || participant?.id || user?.id || ''
  }, [param_user_id, participant?.id, user?.id])

  const hasAdminPermission = useMemo(() => isAdmin || isOrganization, [isAdmin, isOrganization])

  const [queryState, queryHandlers] = useQuery(
    { url: `/note/filter/${user_id}` },
    { initialData: [], isBase: false, skip: !user_id },
  )
  const [pageTableState, pageTableHandlers] = usePageTable<NoteTypeWithStatus>({
    isPaginated: true,
    isBaseQuery: false,
    queryNode: 'note',
    searchableFields: ['message'],
    queryParams: {
      user_id,
      filter_id: filterId,
      ...(filterNoteCategory !== 'all' && { categories: [filterNoteCategory] }),
      sort: [{ column: 'created_date', direction: filterSort }],
    },
  })

  const { tableState, rowResponse } = pageTableState
  const { onSearch, tableDispatch } = pageTableHandlers
  const {
    onCreate,
    onEdit,
    onDelete,
    onRestore,
    onArchive,
    onPreviewAttachment,
    onPreviewSystemNote,
    onPreviewNote,
    onMemberUnFlag,
    onMemberFlag,
  } = useNoteActions({
    user_id,
    hasAdminPermission,
    refetch: pageTableHandlers?.queryHandlers?.refetch,
  })

  const openFilters = useCallback(() => {
    if (queryState.loading) return

    dispatch(
      showDialog({
        component: FilterDialog,
        props: {
          title: '',
          options: queryState.data,
          sortByValue: filterSort,
          notesValue: filterId,
          noteCategoryValue: filterNoteCategory,
          onApply: (data: { sortByValue: 'asc' | 'desc'; notesValue: string; noteCategoryValue: string }) => {
            setFilterSort(data.sortByValue)
            setFilterId(data.notesValue)
            setFilterNoteCategory(data.noteCategoryValue)
            dispatch(hideAllDialogs())
          },
        },
      }),
    )
  }, [queryState, dispatch, setFilterSort, setFilterId, setFilterNoteCategory])

  return (
    <NoteContainer>
      <div className="note-card-container">
        <div className="note-action-container">
          <div className="note-action-select"></div>
          <div className="note-action-buttons">
            <Button
              variant="primary"
              onClick={onCreate({ onSuccess: handleRefetch })}
              // mr="24px"
              py="14px"
              px="18px"
              icon={<SvgNoteIcon fill="#FFF" />}
            >
              Create Note
            </Button>
          </div>
        </div>
        <FilterContainer>
          <NumNotes>{rowResponse.data.count || 0} Notes Found</NumNotes>
          <SearchFilterContainer>
            <SearchField
              id="search-note-field"
              placeholder="Search Notes"
              onChange={(value: ReactText) => pageTableHandlers.onSearch(String(value))}
            />
            <AdvancedFilters onClick={openFilters}>
              <FilterIcon fill={theme.colors.main} />
              <span>Filter</span>
            </AdvancedFilters>
          </SearchFilterContainer>
        </FilterContainer>

        {rowResponse.data.data?.map((note: NoteTypeWithStatus) => (
          <NoteCardData
            participant={participant}
            refetchParticipant={refetchParticipant}
            note={note}
            user_id={user_id}
            hasAdminPermission={hasAdminPermission}
            pageTableHandlers={pageTableHandlers}
            handleRefetch={handleRefetch}
          />
        ))}
      </div>
      <Pagination
        pageSizes={[10, 20, 40, 60, 80]}
        size={tableState.size}
        page={tableState.page}
        count={rowResponse.data.count || 0}
        setSize={(payload: number) => pageTableHandlers.tableDispatch({ type: 'SetSize', payload })}
        setPage={(payload: number) => pageTableHandlers.tableDispatch({ type: 'SetPage', payload })}
      />
    </NoteContainer>
  )

  function handleRefetch() {
    // dispatch(hideDialog())
    if (pageTableHandlers?.queryHandlers?.refetch) pageTableHandlers?.queryHandlers?.refetch()

    if (queryHandlers?.refetch) queryHandlers?.refetch()
  }

  function handleParticipantRefetch() {
    // dispatch(hideDialog())
    if (pageTableHandlers?.queryHandlers?.refetch) pageTableHandlers?.queryHandlers?.refetch()

    if (queryHandlers?.refetch) queryHandlers?.refetch()

    if (refetchParticipant) refetchParticipant()
  }
}

type NoteCardDataProps = {
  participant?: ParticipantTypeWithStatus
  refetchParticipant?: () => void
  note: NoteTypeWithStatus
  user_id: string
  hasAdminPermission: boolean
  pageTableHandlers: UsePageTableHandlers
  handleRefetch: () => void
  // onPreviewSystemNote: (params: Preview) => void
}
export function NoteCardData(props: NoteCardDataProps) {
  const { participant, refetchParticipant, note, user_id, hasAdminPermission, pageTableHandlers, handleRefetch } = props
  const {
    onCreate,
    onEdit,
    onDelete,
    onRestore,
    onArchive,
    onPreviewAttachment,
    onPreviewSystemNote,
    onPreviewNote,
    onMemberUnFlag,
    onMemberFlag,
  } = useNoteActions({
    user_id,
    hasAdminPermission,
    refetch: pageTableHandlers?.queryHandlers?.refetch,
  })
  const theme = useContext(ThemeContext)
  const { auth: user } = useSelector(authSelector)
  const { organization_information } = useSelector(organizationSettingSelector)

  function getNoteActions() {
    return [
      {
        label: 'Edit Note',
        onClick: (data: NoteTypeWithStatus) => onEdit({ onSuccess: handleRefetch })(data),
        conditionalRendering: (data: NoteTypeWithStatus) => data?.status === 'Active' && data?.created_by === user?.id,
      },
      {
        label: 'Restore Note',
        color: '#188C46',
        onClick: (data: NoteTypeWithStatus) => onRestore({ onSuccess: handleRefetch })(data),
        conditionalRendering: (data: NoteTypeWithStatus) => data?.status !== 'Active' && hasAdminPermission,
      },
      {
        label: 'Archive Note',
        color: theme?.colors?.caution,
        onClick: (data: NoteTypeWithStatus) => onArchive({ onSuccess: handleRefetch })(data),
        conditionalRendering: (data: NoteTypeWithStatus) =>
          data?.status === 'Active' &&
          (hasAdminPermission || data?.created_by === user?.id) &&
          !(data?.is_flagged || data?.is_warned),
      },
      {
        label: 'Delete Note',
        color: theme?.colors?.caution,
        onClick: (data: NoteTypeWithStatus) => onDelete({ onSuccess: handleRefetch })(data),
        conditionalRendering: (data: NoteTypeWithStatus) =>
          (hasAdminPermission || data?.created_by === user?.id) && !(data?.is_flagged || data?.is_warned),
      },
    ]
  }

  const isManualFlag = useMemo(() => {
    return note?.type?.includes('Manual Flag') || note?.type?.includes('Generated')
  }, [note])

  const isManualWarnFlag = useMemo(() => {
    return note?.type?.includes('Manual Warn Flag')
  }, [note])

  return (
    <div
      className="note-card-wrapper"
      onClick={() => {
        const pUser = participant as any
        // if (!note?.is_flagged || (note?.is_flagged && note?.comment_count))
        onPreviewNote({ onSuccess: handleRefetch })(note)()

        // if (note?.is_flagged && !note?.comment_count) onPreviewSystemNote({ participant: pUser })(note)()
      }}
      key={note?.id}
    >
      <Avatar
        imageURL={note?.type === 'System Generated' ? DefaultImage : `${note?.created_by_user?.photo_url || ''}`}
      />
      <div
        className={cn('note-card', {
          resolved_flagged: isManualFlag && !note?.is_flagged,
          resolved_warned: isManualWarnFlag && !note?.is_warned,
          flagged: isManualFlag && note?.is_flagged,
          warned: isManualWarnFlag && note?.is_warned,
        })}
      >
        {/*<div className="note-indicator note-indicator--new-comment">
        New Comments
      </div>*/}
        {organization_information.features.note.is_enabled && note.categories && note.categories.length > 0 && (
          <NoteCategoriesContainer>
            {note.categories.map((category: string) => (
              <Category>{category}</Category>
            ))}
          </NoteCategoriesContainer>
        )}
        {((isManualFlag && !note?.is_flagged) || (isManualWarnFlag && !note?.is_warned)) && (
          <Resolved>Resolved</Resolved>
        )}
        <NoteCardHeader className="note-card-header">
          <div className="note-card-header--info">
            {note?.type === 'System Generated' && (
              <div className="note-card-header--user">
                {note?.is_flagged && 'Member Flagged by System'}
                {!note?.is_flagged && (
                  <Fragment>
                    Member Unflagged by {`${note?.unflagged_by_user?.full_name}`}
                    <div className="note-card-header--user-type">
                      (
                      {`${upperFirst(
                        note?.unflagged_by_user?.role_display_name || note?.unflagged_by_user?.role_name,
                      )}`}
                      )
                    </div>
                  </Fragment>
                )}
              </div>
            )}
            {!['System Generated', 'Manual Flag', 'Manual Warn Flag', 'Program/Event Note'].includes(
              note.type || '',
            ) && (
              <div className="note-card-header--user">
                Note added by {`${note?.created_by_user?.full_name}`}
                <div className="note-card-header--user-type">
                  ({`${upperFirst(note?.created_by_user?.role_display_name || note?.created_by_user?.role_name)}`})
                </div>
              </div>
            )}
            {note?.type === 'Manual Flag' && (
              <div className="note-card-header--user">
                {note?.is_flagged && (
                  <Fragment>
                    Member Flagged by {`${note?.created_by_user?.full_name}`}
                    <div className="note-card-header--user-type">
                      ({`${upperFirst(note?.created_by_user?.role_display_name || note?.created_by_user?.role_name)}`})
                    </div>
                  </Fragment>
                )}
                {!note?.is_flagged && (
                  <Fragment>
                    Member Unflagged by {`${note?.unflagged_by_user?.full_name}`}
                    <div className="note-card-header--user-type">
                      (
                      {`${upperFirst(
                        note?.unflagged_by_user?.role_display_name || note?.unflagged_by_user?.role_name,
                      )}`}
                      )
                    </div>
                  </Fragment>
                )}
              </div>
            )}
            {note?.type === 'Manual Warn Flag' && (
              <div className="note-card-header--user">
                {note?.is_warned && (
                  <Fragment>
                    Member Reminder by {`${note?.created_by_user?.full_name}`}
                    <div className="note-card-header--user-type">
                      ({`${upperFirst(note?.created_by_user?.role_display_name || note?.created_by_user?.role_name)}`})
                    </div>
                  </Fragment>
                )}
                {!note?.is_warned && (
                  <Fragment>
                    Member Resolved Reminder by {`${note?.unflagged_by_user?.full_name}`}
                    <div className="note-card-header--user-type">
                      (
                      {`${upperFirst(
                        note?.unflagged_by_user?.role_display_name || note?.unflagged_by_user?.role_name,
                      )}`}
                      )
                    </div>
                  </Fragment>
                )}
              </div>
            )}
            {note?.type === 'Program/Event Note' && (
              <div className="note-card-header--user">
                Program/Event Note added by {`${note?.created_by_user?.full_name}`}
                <div className="note-card-header--user-type">
                  ({`${upperFirst(note?.created_by_user?.role_display_name || note?.created_by_user?.role_name)}`})
                </div>
              </div>
            )}
            <div className="note-card-header--date">{dayjs(note.created_date).format(`MM/DD/YYYY [at] hh:mm A`)}</div>
          </div>
          {(hasAdminPermission || (note?.created_by === user?.id && note?.type !== 'System Generated')) &&
            !(note?.type === 'System Generated' && note?.is_flagged) && (
              <MoreOptions row={note} actions={getNoteActions()} fill="#727272" width="157px" />
            )}
        </NoteCardHeader>
        {hasAdminPermission && Boolean(note?.role_names?.length) && (
          <div className="note-visibilty">
            <SvgVisibilityIcon fill="#727272" />
            Note viewable by {note?.role_names?.join(', ')}
          </div>
        )}
        {note?.appointment && (
          <Fragment>
            <div className="event-note-container">
              <div className="event-note">
                <div className="event-note--key">
                  <SvgCalendarIcon /> Date
                </div>
                <div className="event-note--value">{dayjs(note?.appointment?.start_date).format(`MM/DD/YYYY`)}</div>
              </div>
              <div className="event-note">
                <div className="event-note--key">
                  <SvgTimeIcon /> Time
                </div>
                <div className="event-note--value">
                  {`${dayjs(note?.appointment?.start_date).format('h:mm A')} - ${dayjs(
                    note?.appointment?.end_date,
                  ).format('h:mm A')}`}
                </div>
              </div>
              <div className="event-note">
                <div className="event-note--key">
                  <SvgLocationIcon width="15" height="18" /> Location
                </div>
                <div className="event-note--value">{note?.appointment?.resources?.join(', ')}</div>
              </div>
              <div className="event-note">
                <div className="event-note--key">
                  <SvgCoachIcon /> Coach
                </div>
                <div className="event-note--value">{note?.appointment?.coaches?.join(', ')}</div>
              </div>
            </div>
            <div className="event-note-status">
              <div className="event-note">
                <div className="event-note--key">Attendance</div>
                <div className="event-note--value">{upperFirst(note?.detail?.attendance_type)}</div>
              </div>
            </div>
          </Fragment>
        )}
        {note?.message && <div className="note-content">{note?.message}</div>}
        {note?.attachments?.map(each => (
          <div className="note-indicator note-indicator--attachment" onClick={onPreviewAttachment()(note)}>
            1 Image attached
          </div>
        ))}
        {Boolean(note?.comment_count) && (
          <div className="note-indicator note-indicator--comment">{`+${note?.comment_count} Comments`}</div>
        )}
        {upperFirst(note?.status) !== 'Archive' &&
          dayjs(note.updated_date).format('MM/DD/YYYY hh:mm:ss A') !==
            dayjs(note.created_date).format('MM/DD/YYYY hh:mm:ss A') && (
            <Tooltipped
              tooltipClassName="custom-tooltip"
              label={
                note?.updated_by_user
                  ? `
              Edited by ${note?.updated_by_user?.full_name} (${note?.updated_by_user?.role_display_name ||
                      note?.updated_by_user?.role_name})
              on ${dayjs(note.updated_date).format('MM/DD/YYYY [at] hh:mm A')}
            `
                  : `
              Edited by ${note?.created_by_user?.full_name} (${note?.created_by_user?.role_display_name ||
                      note?.created_by_user?.role_name})
              on ${dayjs(note.updated_date).format('MM/DD/YYYY [at] hh:mm A')}
            `
              }
              position="bottom"
              setPosition
            >
              <div className="note-indicator note-indicator--edited" onClick={onPreviewNote()(note)}>
                <SvgPencilIcon /> Edited
              </div>
            </Tooltipped>
          )}
        {note?.note_comment?.map((comment: NoteComment) => (
          <Fragment>
            {upperFirst(comment?.type) === 'Archive Note' && note?.archive_by_user && (
              <div className="note-additional-detail-container">
                <SvgArchiveIcon />
                <div className="note-additional-detail">
                  <div className="note-additional-detail--info">
                    <div className="note-additional-detail--user">
                      Archived by {`${note?.archive_by_user?.full_name}`}
                      <div className="note-additional-detail--user-type">
                        ({`${upperFirst(note?.archive_by_user?.role_display_name || note?.archive_by_user?.role_name)}`}
                        )
                      </div>
                    </div>
                    <div className="note-additional-detail--date">
                      {dayjs(comment.created_date).format(`MM/DD/YYYY [at] hh:mm A`)}
                    </div>
                  </div>
                  <div className="note-additional-detail--reason">Reason: {`${comment?.message}`}</div>
                </div>
              </div>
            )}
            {(upperFirst(comment?.type) === 'Flag Note' || upperFirst(comment?.type) === 'Warn Note') &&
              note?.unflagged_by_user && (
                <div className="note-additional-detail-container note-additional-detail-container--comment">
                  <div className="note-additional-detail">
                    <div className="note-additional-detail--info">
                      <div className="note-additional-detail--user">
                        Note added by {`${note?.unflagged_by_user?.full_name}`}
                        <div className="note-additional-detail--user-type">
                          (
                          {`${upperFirst(
                            note?.unflagged_by_user?.role_display_name || note?.unflagged_by_user?.role_name,
                          )}`}
                          )
                        </div>
                      </div>
                      <div className="note-additional-detail--date">
                        {dayjs(comment.created_date).format(`MM/DD/YYYY [at] hh:mm A`)}
                      </div>
                    </div>
                    <div className="note-additional-detail--reason">Reason: {`${comment?.message}`}</div>
                  </div>
                </div>
              )}
          </Fragment>
        ))}
      </div>
    </div>
  )
}

const FilterContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
`

const SearchFilterContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const AdvancedFilters = styled(Box)<{ disabled?: boolean }>`
  display: flex;
  justify-content: flex-end;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  margin-left: 10px;
  ${({ disabled, theme }) => `
    color:  ${disabled ? theme.colors.lightGrey : theme.colors.main};
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
  `}

  span {
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    text-align: center;
    text-decoration-line: underline;
  }
`

const NumNotes = styled(Box)`
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
`

const NoteCategoriesContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`

const Category = styled(Box)`
  border-radius: 4px;
  background: #f9f3f8;
  padding: 5px 16px;
  color: #6b0d65;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  margin-bottom: 10px;
  margin-right: 10px;
  min-width: 150px;
`

const Resolved = styled(Box)`
  border-radius: 4px;
  background: #bad8d1;
  padding: 5px 16px;
  color: #238064;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  margin-bottom: 10px;
  width: fit-content;
`

export const NoteCardHeader = styled(Box)`
  &,
  .note-card-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .more-options-item-container {
      top: 24px;
    }

    button {
      height: 0;
      padding-bottom: 24px;
    }

    &--info {
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      justify-content: center;
    }

    &--user {
      font-weight: bold;
      font-size: 16px;
      line-height: 20px;
      color: ${({ theme }) => theme?.colors?.secondary};
    }

    &--user-type {
      font-weight: bold;
      font-size: 16px;
      line-height: 20px;
      color: #727272;
      display: inline-block;
      margin-left: 4px;
    }

    &--date {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      color: #727272;
    }
  }
`
const NoteContainer = styled(Box)`
  margin-top: 60px;

  .note-action-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .note-action-select {
    width: 300px;
  }

  .note-action-buttons {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    margin-top: 28px;
  }

  .note-card-container {
    width: 772px;
    min-height: 85px;
    margin: 80px auto 85px;
  }

  .note-card-wrapper {
    display: flex;
    flex-direction: row;

    &:not(:first-child) {
      margin-top: 24px;
    }
  }

  .note-card {
    padding: 24px;
    min-height: 50px;
    width: 100%;

    background: #fdfdfd;
    border: 1px solid #f9f9f9;
    border-radius: 8px;

    box-sizing: border-box;
    box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.15);
    position: relative;

    &.resolved_flagged,
    &.resolved_warned {
      border: 2px solid #238064;
    }

    &.warned {
      border: 2px solid ${FLAG_WARNING_COLOR};
    }

    &.flagged {
      border: 2px solid ${FLAG_DANGER_COLOR};
    }

    &.resolved_flagged::after {
      content: '';
      position: absolute;
      top: -1px;
      right: -1px;

      width: 0;
      height: 0;
      border-top: 24px solid ${FLAG_RESOLVED_DANGER_COLOR};
      border-left: 24px solid #fdfdfd;
      border-top-right-radius: 8px;
    }

    &.flagged::after {
      content: '';
      position: absolute;
      top: -1px;
      right: -1px;

      width: 0;
      height: 0;
      border-top: 24px solid ${FLAG_DANGER_COLOR};
      border-left: 24px solid #fdfdfd;
      border-top-right-radius: 8px;
    }

    &.resolved_warned::after {
      content: '';
      position: absolute;
      top: -1px;
      right: -1px;

      width: 0;
      height: 0;
      border-top: 24px solid ${FLAG_RESOLVED_WARNING_COLOR};
      border-left: 24px solid #fdfdfd;
      border-top-right-radius: 8px;
    }

    &.warned::after {
      content: '';
      position: absolute;
      top: -1px;
      right: -1px;

      width: 0;
      height: 0;
      border-top: 24px solid ${FLAG_WARNING_COLOR};
      border-left: 24px solid #fdfdfd;
      border-top-right-radius: 8px;
    }
  }

  .avatar-container {
    width: 56px;
    height: 56px;
    min-width: 56px;
    min-height: 56px;

    margin-right: 24px;
    border: 1px solid #dcdcdc;
  }

  .note-content {
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 21px;
    color: ${({ theme }) => theme?.colors?.secondary};
    white-space: pre-wrap;
    margin-top: 1.5rem;
  }

  .note-indicator {
    color: ${({ theme }) => theme?.colors?.main};
    font-style: normal;
    font-weight: bold;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;

    &--attachment {
      text-decoration-line: underline;
      margin-top: 1.5rem;
      width: fit-content;
    }

    &--new-comment {
      color: #188c46;
      background: #d9f2e9;
      width: fit-content;
      border-radius: 4px;
      margin-bottom: 10px;
      padding: 0.25rem 1rem;
      cursor: unset;
    }

    &--comment {
      height: 36px;
      background: ${({ theme }) => lighten(0.6, theme?.colors?.main)};

      border-radius: 200px;
      width: fit-content;
      padding: 0.5rem 1rem;
      margin-top: 1.5rem;
    }

    &--edited {
      display: flex;
      align-items: center;
      color: ${({ theme }) => theme?.colors?.secondary};
      margin-top: 1.2rem;
      width: fit-content;

      svg {
        margin-right: 4px;
      }
    }
  }

  .note-visibilty {
    font-weight: normal;
    font-size: 16px;
    line-height: 21px;
    color: #727272;

    display: flex;
    align-items: center;
    margin: 1rem 0;

    & + * {
      margin-top: 0 !important;
    }

    svg {
      margin-right: 8px;
    }
  }

  .note-additional-detail {
    width: 100%;

    &-container {
      background: rgba(10, 80, 200, 0.06);
      border-radius: 2px;

      margin-top: 1.5rem;
      padding: 1.5rem;
      width: 100%;
      min-height: 95px;

      display: flex;
      align-items: center;

      svg {
        margin-right: 1rem;
      }

      &--comment {
        .note-additional-detail--user {
          color: #727272;
        }

        .note-additional-detail--reason {
          font-weight: normal;
        }
      }
    }

    &--reason {
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;
      color: ${({ theme }) => theme?.colors?.secondary};
      margin-top: 4px;
    }

    &--info {
      width: 100%;
      display: flex;
      justify-content: space-between;
      flex-direction: row;
      align-items: center;
    }

    &--user {
      font-weight: bold;
      font-size: 16px;
      line-height: 20px;
      color: ${({ theme }) => theme?.colors?.secondary};
    }

    &--user-type {
      font-weight: bold;
      font-size: 16px;
      line-height: 20px;
      color: #727272;
      display: inline-block;
      margin-left: 4px;
    }

    &--date {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      color: #727272;
    }
  }

  .event-note {
    display: flex;
    flex-direction: row;
    align-items: center;

    font-size: 16px;
    line-height: 24px;
    color: ${({ theme }) => theme?.colors?.secondary};

    &--key {
      font-weight: bold;
      display: flex;
      align-items: center;

      min-width: 95px;
      margin-right: 24px;

      svg {
        margin-right: 8px;
      }
    }

    &--value {
      font-weight: 500;
    }

    &-status {
      margin-top: 1.5rem;
    }

    &-container {
      display: grid;
      grid-template-rows: auto auto;
      grid-template-columns: repeat(auto-fit, minmax(272px, 1fr));
      row-gap: 0.5rem;

      margin-top: 1.5rem;
    }
  }
`

export default Notes
